/**
 * The API client — the ONLY way this app reaches data.
 *
 * Hard rule 4: the frontend holds no database connection, no ORM client and
 * no connection string. `pnpm boundaries` fails the build if that ever
 * changes, because a connection string shipped to a browser is a connection
 * string shipped to every visitor.
 */
import { apiUrl } from './tenant.js';
import type { Locale } from '@myrave/shared';
import { translate } from '@/i18n';
import { NETWORK_TIMEOUT_MS, withTimeout } from '@/offline/net';
import { pendingFamily, revokePending, revokeSettled } from './signOut.js';
import { noteServerReached } from './reach';
import { isFramed, PREVIEW_READ_ONLY } from './framed';
import { askParentSession } from './framedSession';
import { inPreview } from './preview';
import { markSignedIn, signedInHere } from './signOut';
import { readJson, writeJson } from './storage';
import { localeHeader } from './shownLocale';
import { passHeader } from './dataPass';
import { keptRaves, noteKeptRaves, withKeptRaves } from './keptRaves';
export { PREVIEW_READ_ONLY } from './framed';

/**
 * The language the page is in, for the one sentence this file writes itself.
 *
 * A refusal's message is the SERVER's and is passed through as it was sent;
 * only the fallback for an answer that carried none is ours. This module has
 * no React, so it reads what `LocaleProvider` wrote on `<html lang>` — the
 * same fact, set in one place.
 */
function pageLocale(): Locale {
  const lang = typeof document === 'undefined' ? '' : document.documentElement.lang;
  return (lang || 'en') as Locale;
}

/**
 * Translate outside React, in the language the page is showing (step 73). Bound as `tr` where it is used
 * (`const tr = shownTr;`), so the coverage test reads its keys as it reads every other call.
 */
export function shownTr(english: string, vars?: Readonly<Record<string, string | number>>): string {
  return translate(pageLocale(), english, vars);
}
const tr = shownTr;

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
    /**
     * The whole refusal, for the few answers that carry more than a message.
     * A failed purchase is a 402 whose body is the payment itself.
     */
    readonly body?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * The API's code for "this account has not finished itself" — the server-side
 * completion gate (`backend/src/http/completion.ts`).
 *
 * The app normally never meets it: `CompleteAccount` asks `GET /me/completion`
 * before rendering anything. It can still arrive — a check that failed offline
 * let the app through, or the requirements changed while a tab was open — and
 * then every request answers 403. Those 403s are not errors to show one by one;
 * they are the server saying "finish first", so they are routed to the one
 * screen that can act on it.
 */
export const ACCOUNT_INCOMPLETE = 'account_incomplete';

const incompleteListeners = new Set<() => void>();

/** Be told when any request is refused with `account_incomplete`. */
export function onAccountIncomplete(listener: () => void): () => void {
  incompleteListeners.add(listener);
  return () => { incompleteListeners.delete(listener); };
}

/**
 * The API's codes for "this admin's sign-in has not passed its authenticator"
 * (step 70, `backend/src/http/mfa.ts`). `RequireMfa` asks `/me/mfa` up front,
 * so these arrive only when something changed under an open tab (a new admin
 * role, a reset) — and then every request answers 403, routed to the one
 * screen that can act on it.
 */
// `mfa_recheck`: an admin console after 10 minutes of an inactive session asks for the passkey or the code again (2026-11-02).
export const MFA_CODES = new Set(['mfa_required', 'mfa_setup_required', 'mfa_recheck']);
const mfaListeners = new Set<() => void>();

/** Be told when any request is refused for want of the authenticator. */
export function onMfaRequired(listener: () => void): () => void {
  mfaListeners.add(listener);
  return () => { mfaListeners.delete(listener); };
}

/*
 * Every admin WRITE that went through, said to whoever listens (the user,
 * 2026-10-11: the wizard's phone preview did not reload after an AI logo and
 * cover were set). The preview reloads on these — whichever screen, model or
 * button made the change — so no new way of changing a rave has to remember
 * to tell it. Not: the wizard's own position (`/setup`), nor an AI question
 * or a logo cut (`/admin/ai/ask|live|logo-*`), which change nothing shown.
 */
const writeListeners = new Set<(path: string) => void>();
const NOT_A_CHANGE = /^\/admin\/(ai\/(ask|live|logo-)|editions\/[^/]+\/setup(\/|$|\?))/;

/** Be told after every successful admin write (POST / PUT / PATCH / DELETE under `/admin/`). */
export function onAdminWrite(listener: (path: string) => void): () => void {
  writeListeners.add(listener);
  return () => { writeListeners.delete(listener); };
}

let accessToken: string | null = null;
/**
 * The server has said "nobody is signed in" (step 78's walk, 2026-10-27): a signed-out rave page
 * asked `/me/festivals` twelve times, each a 401 and a second trip to `/auth/refresh`. While this
 * holds, a `/me/` read is answered here (`request`). Any token lifts it (`setAccessToken`).
 */
let signedOutKnown = false;
/** When the access token in memory stops working, epoch ms — from its own `exp`. */
let expiresAt: number | null = null;
let expiryTimer: ReturnType<typeof setTimeout> | null = null;

/** Refresh this long before the token expires, so a request never meets the edge. */
const REFRESH_LEAD_MS = 60_000;

/**
 * The `exp` claim of a JWT, epoch ms — or null for anything that is not one.
 *
 * Read, never trusted: the server checks the signature, and all this decides
 * is WHEN to ask for a new token. A token that will not decode simply gets no
 * proactive refresh and falls back on the 401 retry.
 */
function tokenClaims(token: string): { exp?: unknown; sub?: unknown; fam?: unknown; sid?: unknown } | null {
  try {
    const part = token.split('.')[1];
    if (!part) return null;
    return JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/'))) as { exp?: unknown; sub?: unknown; fam?: unknown; sid?: unknown };
  } catch {
    return null;
  }
}

function tokenExpiry(token: string): number | null {
  const claims = tokenClaims(token);
  return typeof claims?.exp === 'number' ? claims.exp * 1000 : null;
}

/**
 * The account the access token in memory belongs to (its `sub`), or null.
 *
 * What the sync queue sends AS: a queued save goes only to the account it was
 * made by (`offline/sync.ts`). Read, not trusted — the server authenticates the
 * request by the token's signature, and a wrong guess here could only mean
 * sending nothing.
 */
export function sessionUserId(): string | null {
  if (!accessToken) return null;
  const sub = tokenClaims(accessToken)?.sub;
  return typeof sub === 'string' ? sub : null;
}

/**
 * The account this device is being used as — with or without a token.
 *
 * Offline there is no token, but there IS a person: `AuthProvider` shows the
 * rave as the account remembered on this origin, and a heart tapped then is
 * theirs. `AuthProvider` sets this whenever its user changes; the sync queue
 * tags each change with it at the moment it is made.
 */
let currentAccount: string | null = null;
export function setCurrentAccount(id: string | null): void {
  currentAccount = id;
}
export function currentAccountId(): string | null {
  return currentAccount ?? sessionUserId();
}

/**
 * The access token lives in memory only.
 *
 * Not localStorage: anything stored there is readable by any script that ends
 * up on the page. The refresh token is the thing that survives a reload, and
 * it is an httpOnly cookie this code cannot read, write or leak — see
 * `backend/src/modules/auth/cookies.ts`, and `AuthProvider` for the exchange
 * on boot.
 *
 * That cookie is why every request here sends `credentials: 'include'`: the
 * app is on a festival subdomain and the API on `api.`, so it is cross-origin
 * and the browser would otherwise omit it.
 */
export function setAccessToken(token: string | null): void {
  accessToken = token;
  if (token) signedOutKnown = false;
  expiresAt = token ? tokenExpiry(token) : null;
  if (expiryTimer) clearTimeout(expiryTimer);
  expiryTimer = null;
  /*
   * The access token lasts `JWT_ACCESS_TTL` (15 minutes) and used to be
   * refreshed exactly once, at boot — so a tab open for a set and a half
   * started answering 401 to everything, and the sync queue stopped with it.
   * A minute before it expires, a new one is asked for. A phone asleep at that
   * minute wakes to `visibilitychange`, which `AuthProvider` answers the same
   * way, and anything that still meets a 401 refreshes and retries once.
   */
  if (expiresAt !== null) {
    const lead = Math.max(0, expiresAt - Date.now() - REFRESH_LEAD_MS);
    expiryTimer = setTimeout(() => { expiryTimer = null; void refreshSession().catch(() => {}); }, lead);
  }
}
export function hasSession(): boolean {
  return accessToken !== null;
}
/** No token, or one that expires within `marginMs`: worth refreshing now. */
export function sessionStale(marginMs = REFRESH_LEAD_MS): boolean {
  return accessToken === null || (expiresAt !== null && expiresAt - Date.now() < marginMs);
}

/* --------------------------------------------------------- the refresh */

/** What `/auth/refresh` answers with a session. The user is `AuthProvider`'s type. */
export interface SessionAnswer {
  accessToken: string;
  user: { id: string; email: string | null; role: string; displayName: string | null; approved: boolean; avatarUrl: string | null };
  /** The account's completion state, as `GET /me/completion` answers it (`CompleteAccount`). */
  completion?: unknown;
  /**
   * The authenticator's state for this sign-in, as `GET /me/mfa` answers it
   * (step 70, `app/RequireMfa.tsx`). Absent from a borrowed framed session and
   * from an API older than 2026-10-28.
   */
  mfa?: { required: boolean; enrolled: boolean; passed: boolean; next?: 'passkey' | 'totp' | null; codeInstead?: boolean };
}

/*
 * The completion state the last refresh carried, for ITS account — taken once
 * by `CompleteAccount` instead of asking `/me/completion` as a second round
 * trip the first screen waited on (step 14, 2026-10-05).
 */
/**
 * **The authenticator's state the last session answer carried** — read, not
 * taken, because the gate re-renders and must give the same answer each time.
 *
 * `RequireMfa` used to ask `GET /me/mfa` and render the app while it was in
 * flight, so an admin saw the whole console paint before the code screen
 * replaced it (the user, 2026-10-28: "if user is admin and need 2fa you cant
 * load the page"). The boot refresh already knows — a rave admin's `role` is
 * `USER`, so only the server can say — and this is where it says it.
 */
export function sessionMfa(userId: string): SessionAnswer['mfa'] | null {
  return lastSession && lastSession.user.id === userId ? lastSession.mfa ?? null : null;
}

/**
 * **A session was given — on any host, by any way in** — and every host of this device learns whose,
 * and whether it owes a second step (`markSignedIn`'s `.m`, `RequireMfa`). The boot refresh calls it;
 * so do the sign-ins whose answer comes to the page itself and then leaves for another host (the
 * password code, the phone code, Google One Tap): the page they land on must know BEFORE its own boot
 * refresh has answered that an admin is arriving, or it draws the app first (the user, 2026-10-29).
 */
export function noteSignIn(answer: { user: { id: string }; mfa?: { required: boolean } | null }): void {
  markSignedIn(answer.user.id, Date.now(), answer.mfa?.required === true);
}

/**
 * A session the PAGE was handed (`AuthProvider`'s `adopt` — the password code, a registration): kept as
 * the boot refresh's is, so `sessionMfa` answers for it too and the gate's first render is right.
 */
export function adoptSession(session: SessionAnswer): void {
  lastSession = session;
  noteSignIn(session);
}

let sessionCompletion: { userId: string; state: unknown } | null = null;
export function takeSessionCompletion(userId: string): unknown {
  const known = sessionCompletion;
  if (!known || known.userId !== userId) return null;
  sessionCompletion = null;
  return known.state;
}

/**
 * A refresh that did not HEAR from the server — no signal, a hang, a 5xx.
 * Different from "you are signed out", which is an answer.
 */
export function isNetworkFailure(err: unknown): boolean {
  if (err instanceof ApiError) return err.status >= 500 || err.status === 408 || err.status === 429;
  return true;
}

/** A hung exchange is abandoned after this, so the single flight can clear. */
const REFRESH_ABANDON_MS = 30_000;

let refreshing: Promise<SessionAnswer | null> | null = null;
/** The last session a refresh gave — handed to the wizard's preview (`sessionForFrame`). */
let lastSession: SessionAnswer | null = null;

/**
 * The page's session for the setup wizard's framed preview (`lib/framedSession.ts`):
 * refreshed first when it is about to expire, so the frame never refreshes itself.
 */
export async function sessionForFrame(): Promise<SessionAnswer | null> {
  if (sessionStale()) return refreshSession();
  return lastSession && accessToken ? { ...lastSession, accessToken } : null;
}
const sessionListeners = new Set<(session: SessionAnswer | null) => void>();

/**
 * Be told whenever a refresh SETTLES WITH AN ANSWER: a session (with its
 * user), or null for "the server says nobody is signed in". A refresh that
 * failed to reach the server tells nobody — nothing is known that was not
 * known before. A refresh that timed out for its caller and landed later
 * still tells, which is how an offline launch picks its session back up.
 */
export function onSession(listener: (session: SessionAnswer | null) => void): () => void {
  sessionListeners.add(listener);
  return () => { sessionListeners.delete(listener); };
}

/** How long a sign-out's revoke may take — sign-out must never hang (`AuthProvider`). */
export const LOGOUT_TIMEOUT_MS = NETWORK_TIMEOUT_MS;

/**
 * Revoke the refresh cookie this browser holds. Resolves once the server has
 * done it, and clears the pending flag; rejects when it could not be heard,
 * leaving the flag for the next exchange on any host to send again.
 */
export async function revokeSession(timeoutMs = LOGOUT_TIMEOUT_MS): Promise<void> {
  // Naming the sign-in it ends: the server leaves a newer one alone (`pendingFamily`).
  const family = pendingFamily();
  try {
    await withTimeout(request('/auth/logout', { method: 'POST', body: JSON.stringify(family ? { family } : {}) }), timeoutMs);
  } catch (err) {
    /*
     * **A REFUSAL settles it; only "did not hear" keeps it owed.**
     *
     * This cleared the flag on success alone, so any 4xx left it set — and
     * while it is set no host refreshes: every boot sends the revoke first and
     * answers "nobody is signed in" (`exchange`). That is a sign-in loop no
     * amount of signing in escapes, and it is reachable: `/auth/logout` has
     * been limited to 20 a minute per refresh token since 2026-10-28, which a
     * loop sending one revoke per host boot passes in seconds. Measured on the
     * user's machine the same evening: 40 sign-ins, each asking for the
     * authenticator again, each refusing the code already on screen.
     *
     * A 4xx means the server HEARD us — the token is unknown, already dead, or
     * throttled — and the design already holds that a new session supersedes an
     * owed revoke (`lib/signOut.ts`, and the API clears the flag on every
     * session it issues). A 5xx or no answer at all is the case the flag is
     * for, and still keeps it.
     *
     * What this gives up: a sign-out that was rate-limited leaves that session
     * alive until it expires. The limit is per token and per minute, so one
     * honest sign-out never reaches it — only a loop does, and a loop is what
     * this prevents.
     */
    if (isNetworkFailure(err) && !(err instanceof ApiError && err.status === 429)) throw err;
  }
  revokeSettled();
}

/** The sign-in the current access token belongs to (`fam`, else `sid`) — or null. */
export function sessionFamily(): string | null {
  if (!accessToken) return null;
  const claims = tokenClaims(accessToken);
  const fam = claims?.fam ?? claims?.sid;
  return typeof fam === 'string' ? fam : null;
}

function exchange(): Promise<SessionAnswer | null> {
  /*
   * **A public page's preview of the rave app is signed out, always**
   * (`lib/preview.ts`): it asks no parent and presents no cookie.
   */
  if (inPreview()) {
    setAccessToken(null);
    return Promise.resolve(null).finally(() => { refreshing = null; });
  }
  /*
   * **Framed (the wizard's preview), the PAGE's session is borrowed — the refresh
   * cookie is never touched here** (the user, 2026-10-13: "it logs me out when
   * accessing the wizard"). The frame reloads after every save; a reload that cut a
   * refresh off left the rotated cookie unreceived, and the page's next refresh
   * then presented a revoked one and ended the whole sign-in.
   */
  if (isFramed()) {
    return askParentSession()
      .then((borrowed) => {
        const session = borrowed ? { accessToken: borrowed.accessToken, user: borrowed.user } as SessionAnswer : null;
        setAccessToken(session?.accessToken ?? null);
        for (const cb of sessionListeners) cb(session);
        return session;
      })
      .finally(() => { refreshing = null; });
  }
  /*
   * **A sign-out that never reached the server comes first.** Refreshing now
   * would present the very cookie the person signed out of — alive, because
   * the revoke was made offline — and sign them back in without a word. So
   * the revoke is sent instead, and the answer is "nobody is signed in". If it
   * cannot be sent either, this is a refresh that did not hear the server:
   * offline, still signed out, and the next exchange tries again.
   */
  if (revokePending()) {
    return revokeSession(REFRESH_ABANDON_MS)
      .then(() => {
        setAccessToken(null);
        for (const cb of sessionListeners) cb(null);
        return null;
      })
      .finally(() => { refreshing = null; });
  }
  const controller = new AbortController();
  const abandon = setTimeout(() => controller.abort(), REFRESH_ABANDON_MS);
  return request<SessionAnswer | { authenticated: false }>('/auth/refresh', {
    method: 'POST', body: JSON.stringify({}), signal: controller.signal,
  })
    .then(
      (result) => {
        /*
         * **A 200 saying "nobody is signed in" is the anonymous answer**, not
         * a session. The endpoint stopped answering 401 for a request with no
         * credential at all — the commonest state on this platform.
         */
        const session = 'accessToken' in result ? result : null;
        lastSession = session;
        // Every tab and host learns who this browser is signed in as (`AuthProvider`).
        // The sign-in-loop brake needs no clearing here: it is this TAB's own
        // short-lived note (`app/RequireSession.tsx`), and a gate only reads it
        // when there is no session at all.
        if (session) noteSignIn(session);
        sessionCompletion = session?.completion ? { userId: session.user.id, state: session.completion } : null;
        setAccessToken(session?.accessToken ?? null);
        signedOutKnown = session === null;
        for (const cb of sessionListeners) cb(session);
        return session;
      },
      (err: unknown) => {
        // The server ANSWERED no (401, 403): signed out, and that is a fact.
        if (!isNetworkFailure(err)) {
          setAccessToken(null);
          signedOutKnown = true;
          for (const cb of sessionListeners) cb(null);
          return null;
        }
        throw err;
      },
    )
    .finally(() => { clearTimeout(abandon); refreshing = null; });
}

/**
 * Exchange the refresh cookie for a new access token — at most one request
 * in flight, however many callers ask at once.
 *
 * **Single flight is not an optimisation.** `/auth/refresh` ROTATES: two
 * concurrent calls send the same cookie, the second finds it already revoked,
 * and the session is gone. Six requests meeting one expired token must share
 * one refresh.
 *
 * Resolves with the session, or null when the server says nobody is signed
 * in. REJECTS when the server could not be heard within `timeoutMs` (the
 * pilot's 2.5s) — and the request keeps going: if it lands later, `onSession`
 * listeners still get it.
 */
export function refreshSession(timeoutMs = NETWORK_TIMEOUT_MS): Promise<SessionAnswer | null> {
  refreshing ??= exchange();
  return withTimeout(refreshing, timeoutMs);
}

/*
 * The page's FIRST refresh, started as the app's code runs (step 14,
 * 2026-10-05) — not when `AuthProvider` mounts, which is under the tenant
 * lookup: a first visit ran tenant → refresh → bundle in series. It is the
 * same single flight as `refreshSession`, so nothing rotates twice, and
 * `AuthProvider`'s boot takes this answer instead of asking again. Only the
 * app starts it (`App.tsx` `prefetchThisHost`); a test never does, so every
 * render in a suite still makes its own boot refresh.
 */
let early: Promise<SessionAnswer | null> | null = null;

/** Start the boot refresh now. */
export function startEarlyRefresh(): void {
  if (early) return;
  refreshing ??= exchange();
  early = refreshing;
  early.catch(() => {});
}

/** The early boot refresh, with the caller's deadline — or null if none was started. */
export function earlyRefresh(timeoutMs = NETWORK_TIMEOUT_MS): Promise<SessionAnswer | null> | null {
  return early ? withTimeout(early, timeoutMs) : null;
}

/** The refresh in flight, if any — for leaving the page without stranding it. */
export function refreshInFlight(): Promise<SessionAnswer | null> | null {
  return refreshing;
}
/** The bearer header, for the few requests that cannot go through `api` (the bundle's ETag). */
export function authHeader(): Record<string, string> {
  return accessToken ? { authorization: `Bearer ${accessToken}` } : {};
}

/**
 * The setup wizard's framed preview is READ-ONLY (round 7, the user's point 51:
 * "remove all the options to touch in the preview"): a framed app sends no
 * write at all — refused here, before the network, for every caller. Only the
 * session's own refresh passes, which reads who is looking.
 */
function refusedInPreview(path: string, method: string): boolean {
  return isFramed() && method !== 'GET' && method !== 'HEAD' && path !== '/auth/refresh';
}

/**
 * A public page's preview (`lib/preview.ts`) is signed out by design: every
 * signed-in read would be a 401 per frame (saves, the people count,
 * entitlements, managed raves…). It asks only for what is open.
 */
const PREVIEW_OPEN = ['/public/', '/tenant', '/loading-lines', '/version', '/auth/providers', '/auth/platform'];
function skippedInPreview(path: string): boolean {
  return inPreview() && !PREVIEW_OPEN.some((p) => path === p || path.startsWith(p));
}

async function request<T>(path: string, init: RequestInit = {}, retried = false): Promise<T> {
  const method = String(init.method ?? 'GET').toUpperCase();
  if (refusedInPreview(path, method)) {
    throw new ApiError(403, PREVIEW_READ_ONLY, tr('This is a preview — nothing can be changed here.'));
  }
  if (skippedInPreview(path)) throw new ApiError(401, PREVIEW_READ_ONLY, tr('A preview asks for nothing signed in.'));
  // Signed out, and the server has said so: a personal read is the server's own 401, asked of nobody.
  // A read made while the first refresh is still out waits for its answer (≤ 2.5 s) — a page's early
  // reads were five 401s on staging (2026-10-27); signed in, they now go out with the token.
  if (!accessToken && method === 'GET' && path.startsWith('/me/')) {
    if (!signedOutKnown && refreshing) await withTimeout(refreshing, NETWORK_TIMEOUT_MS).catch(() => null);
    if (signedOutKnown && !accessToken) throw new ApiError(401, 'unauthorized', 'unauthorized');
  }
  /*
   * **A sign-in that still owes its authenticator asks nothing of the server
   * either** — the same rule one line up, for the other state in which every
   * personal read is already decided.
   *
   * `http/mfa.ts` refuses every request of an account holding an admin role
   * until THIS sign-in has passed its code, so each of these is a doomed round
   * trip. The user, 2026-10-29: "why after google sign in i am getting 4 403
   * errors?" — `/me/managed-raves` and `/me/entitlements` from
   * `LocaleProvider`, `/me/profile` twice from `AuthProvider`'s session
   * listener. None is inside `RequireMfa`: the locale provider sits above the
   * tenant (and so above the gate) and the listener fires the moment a token
   * exists, so holding the gate's screen cannot stop them.
   *
   * The session answer carries the state (`mfa`, `backend/.../auth/mfaState.ts`),
   * so this is known without asking. Refusing locally keeps the code the gate
   * needs (`/me/mfa*`) and the way out (`/auth/*`) going.
   */
  if (method === 'GET' && path.startsWith('/me/') && !path.startsWith('/me/mfa')) {
    const mfa = lastSession?.mfa;
    if (mfa?.required && !mfa.passed) {
      throw new ApiError(403, 'mfa_required', tr('Finish signing in first.'));
    }
  }
  const res = await fetch(`${apiUrl()}${path}`, {
    ...init,
    credentials: 'include',
    headers: {
      // A form (images with their message) sets its own type, with its boundary.
      ...(init.body instanceof FormData ? {} : { 'content-type': 'application/json' }),
      ...(accessToken ? { authorization: `Bearer ${accessToken}` } : {}),
      // The page's language, so a refusal's words come back in it (`shownLocale.ts`).
      ...localeHeader(),
      // The data pass, when this tab earned one (step 99, `dataPass.ts`).
      ...passHeader(),
      ...init.headers,
    },
  });
  // Any answer, a refusal included, came from the server (the version matrix).
  noteServerReached();

  /*
   * An expired access token: refresh once and ask again. Never for `/auth/*`
   * itself — a refused password is an answer, and a refresh that 401s must
   * not refresh. If the refresh cannot reach the server, the original 401
   * stands and the caller sees it.
   */
  if (res.status === 401 && !retried && !path.startsWith('/auth/')) {
    const session = await refreshSession().catch(() => null);
    if (session) return request<T>(path, init, true);
  }

  if (res.status === 204) {
    if (method !== 'GET' && path.startsWith('/admin/') && !NOT_A_CHANGE.test(path)) {
      for (const listener of writeListeners) listener(path);
    }
    return undefined as T;
  }

  const body = await res.json().catch(() => ({}));
  /*
   * **The second step's answer replaces the one the boot refresh carried** (the user, 2026-10-29,
   * the third time: back from signing in, an admin passed the code and My raves said 0 until a
   * reload). The refusal above reads `lastSession.mfa`, and only a refresh wrote it — so after the
   * code went through every `/me/` read was still refused here, asked of nobody, until the next page.
   */
  if (res.ok && method === 'GET' && path === '/me/mfa' && lastSession?.mfa) {
    const s = body as Partial<NonNullable<SessionAnswer['mfa']>>;
    if (typeof s.passed === 'boolean') {
      lastSession = { ...lastSession, mfa: { ...lastSession.mfa, required: s.required ?? lastSession.mfa.required, enrolled: s.enrolled ?? lastSession.mfa.enrolled, passed: s.passed, next: s.next ?? null } };
    }
  }
  if (!res.ok) {
    const code = (body as { error?: string }).error ?? 'error';
    if (res.status === 403 && code === ACCOUNT_INCOMPLETE) {
      for (const listener of incompleteListeners) listener();
    }
    if (res.status === 403 && MFA_CODES.has(code)) {
      for (const listener of mfaListeners) listener();
    }
    throw new ApiError(
      res.status,
      code,
      (body as { message?: string }).message
        ?? translate(pageLocale(), 'The request failed ({status}).', { status: res.status }),
      (body as { details?: unknown }).details,
      body,
    );
  }
  if (method !== 'GET' && path.startsWith('/admin/') && !NOT_A_CHANGE.test(path)) {
    for (const listener of writeListeners) listener(path);
  }
  return body as T;
}

export interface NotificationRow {
  id: string;
  kind: string;
  title: string;
  body: string | null;
  data: Record<string, unknown>;
  readAt: string | null;
  createdAt: string;
  /** CRITICAL / HIGH / NORMAL / LOW (§37.5); CRITICAL fills the screen (§37.83). */
  priority?: string;
  /** The rave it is about, or none for a platform-wide one (§37.14). */
  editionId?: string | null;
  /** "5 changes in OZORA's lineup" is one row standing for five (§37.42). */
  groupCount?: number;
  expiresAt?: string | null;
  /** Withdrawn by its sender (§37.68). */
  cancelledAt?: string | null;
  /** Replaced by a newer notice (§37.69). */
  supersededById?: string | null;
}

export interface NotificationPage {
  notifications: NotificationRow[];
  nextCursor: string | null;
  unread: number;
  /** With `?editionId=`: that rave's unread count (step 61). */
  unreadHere?: number;
}

export interface NotificationPreferences {
  preferences: Record<string, boolean>;
  /** Kinds that cannot be switched off. Rendered locked, not unchecked. */
  alwaysOn: string[];
  kinds: string[];
  /** Each kind's EMAIL switch (the user, 2026-10-07, A3). */
  email?: Record<string, boolean>;
  /** System email — payments: always emailed, never unsubscribable. */
  systemEmail?: string[];
  /** Kinds never emailed (set reminders): no email switch at all. */
  neverEmailed?: string[];
}

export interface AccessDecision {
  granted: boolean;
  /** `comp` is a legacy grant — coupons replaced comps (the user, 2026-10-07). */
  reason: 'free' | 'purchased' | 'comp' | 'subscription' | 'master' | 'none';
  expiresAt?: string;
}

export interface PaymentRow {
  id: string;
  amountCents: number;
  currency: string;
  /** Formatted by the API — the same string a receipt carries. */
  amount: string;
  status: 'PENDING' | 'SUCCEEDED' | 'FAILED' | 'REFUNDED' | 'DISPUTED' | string;
  provider?: string;
  description: string | null;
  editionId?: string | null;
  createdAt: string;
  /** Why it failed, in the provider's words. Only on FAILED. */
  failureReason?: string | null;
  /**
   * What the buyer paid in their own currency, formatted, when Stripe
   * Adaptive Pricing converted the dollar price — `amount` stays the dollars
   * MyRave records (the user, 2026-10-07).
   */
  paidAmount?: string | null;
  /** Money returned so far, formatted, when any was. */
  refunded?: string | null;
}

/**
 * What `POST /editions/:id/purchase` answers.
 *
 * `PENDING` with a `clientSecret` is a card payment waiting for the payer's
 * browser to confirm it through Stripe.js; the webhook then settles it, and
 * the app learns that by asking `GET /editions/:id/access` again.
 */
export interface PurchaseResponse {
  payment: {
    id: string;
    status: 'SUCCEEDED' | 'PENDING' | 'FAILED' | 'ALREADY_GRANTED' | string;
    clientSecret?: string;
    redirectUrl?: string;
    /** The provider's reason, on a FAILED payment. */
    failureReason?: string;
  };
  access: AccessDecision;
  publishableKey?: string;
}

export const api = {
  /** `headers`: a request's own — the locale an answer is wanted in, when the page may not show it yet. */
  get: <T>(path: string, headers?: Record<string, string>) => request<T>(path, headers ? { headers } : {}),
  /** `signal`: the caller's deadline — a send that may hang (a support ticket) aborts and tries again. */
  post: <T>(path: string, body?: unknown, signal?: AbortSignal) =>
    request<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body), ...(signal ? { signal } : {}) }),
  put: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
  /**
   * PUT raw bytes — one file, no fields.
   *
   * Not multipart: a profile picture is a single blob with nothing beside it,
   * so a form encoding would be a parser to maintain on both sides for nothing.
   * **`application/octet-stream`, deliberately, and not the file's own type.**
   * The server decides the format from the BYTES (`modules/users/avatar.ts`)
   * because a declared type is whatever the client says — so sending one here
   * would be a claim it is right to ignore, and `application/json` (this
   * client's default for everything else) would be a claim that is simply
   * false.
   */
  putBytes: <T>(path: string, body: Blob) =>
    request<T>(path, {
      method: 'PUT',
      body,
      headers: { 'content-type': 'application/octet-stream' },
    }),
  /**
   * POST raw bytes WITH their declared type — the photo search's selfie.
   *
   * Unlike `putBytes`, the type is sent: `POST /editions/:id/photos/search`
   * reads it to refuse anything but JPEG, PNG or WebP before the bytes reach a
   * matcher. The server still checks the size; nothing here is trusted.
   */
  postBytes: <T>(path: string, body: Blob | string, contentType: string) =>
    request<T>(path, {
      method: 'POST',
      body,
      headers: { 'content-type': contentType },
    }),
  patch: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  /** POST a multipart form — a support message with its images (step 69). */
  postForm: <T>(path: string, form: FormData, headers: Record<string, string> = {}, signal?: AbortSignal) =>
    request<T>(path, { method: 'POST', body: form, headers, ...(signal ? { signal } : {}) }),
  /**
   * GET raw bytes with the session — a bought photograph's original, which
   * has no public URL at all (the user, 2026-10-07: a private link behind
   * sign-in, for the buyer only). The bytes come back as a Blob the screen
   * hands to the browser; an expired token is refreshed once, as `request`
   * does.
   */
  getBlob: async (path: string): Promise<Blob> => {
    const fetchIt = () => fetch(`${apiUrl()}${path}`, { credentials: 'include', headers: { ...authHeader(), ...localeHeader() } });
    let res = await fetchIt();
    noteServerReached();
    if (res.status === 401 && (await refreshSession())) res = await fetchIt();
    if (!res.ok) {
      const body = await res.json().catch(() => ({})) as { error?: string; message?: string };
      throw new ApiError(res.status, body.error ?? 'request_failed', body.message ?? `request failed (${res.status})`);
    }
    return res.blob();
  },
  /**
   * POST raw bytes and get raw bytes back — the wizard's logo cut-out, which
   * sends a close-up and receives the logo as a PNG (2026-10-11). As
   * `getBlob`: the session, one refresh on a 401, the API's error as an
   * `ApiError`.
   */
  postBytesForBlob: async (path: string, body: Blob, contentType: string): Promise<Blob> => {
    const send = () => fetch(`${apiUrl()}${path}`, {
      method: 'POST', credentials: 'include', body, headers: { ...authHeader(), ...localeHeader(), 'content-type': contentType },
    });
    let res = await send();
    noteServerReached();
    if (res.status === 401 && (await refreshSession())) res = await send();
    if (!res.ok) {
      const err = await res.json().catch(() => ({})) as { error?: string; message?: string };
      throw new ApiError(res.status, err.error ?? 'request_failed', err.message ?? `request failed (${res.status})`);
    }
    return res.blob();
  },
  /** A body is optional and rarely right, but `DELETE /me/push-subscriptions`
   *  identifies the device by its endpoint, which is too long for a query. */
  del: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'DELETE', body: body === undefined ? undefined : JSON.stringify(body) }),
};

// ---- typed endpoints -------------------------------------------------------

export interface FestivalCard {
  id: string; slug: string; subdomain: string; name: string;
  description: string | null; country: string | null;
  /** How many people come to the rave (public sources, not MyRave's users); null = not known. */
  crowdSize?: number | null;
  /** What kind of rave — slugs of `RAVE_TYPES` (@myrave/shared); empty = not known. */
  types?: string[];
  logoUrl: string | null; coverUrl: string | null;
  edition: {
    id: string; slug: string; name: string; venue: string | null; timezone: string;
    gatesOpenAt: string | null; endsAt: string | null;
    isFree: boolean; actCount: number; stageCount: number; lineupAnnounced: boolean;
    /** A TEST rave — a demo copy; its card says TEST (2026-10-26). */
    test?: boolean;
    /** The countdown module is on — the card's pill is its (§52.1). */
    countdown?: boolean;
  } | null;
  previousEdition: { id: string; slug: string; name: string; actCount: number } | null;
  /** The edition the bare subdomain opens. One entry per EDITION, not per festival. */
  default?: boolean;
}

/** A published edition of the tenant's festival — what `/<slug>` may name. */
export interface TenantEdition {
  id: string; slug: string; name: string; timezone: string;
  gatesOpenAt: string | null; endsAt: string | null;
  /** This year's logo, else the festival's — what the loading screen shows before the bundle. */
  logoUrl?: string | null;
  venue?: string | null;
}

export interface SearchResults {
  query: string;
  festivals: {
    kind: 'festival'; id: string; slug: string; subdomain: string; name: string;
    country?: string; venue?: string; editionSlug?: string; editionName?: string;
    gatesOpenAt?: string; endsAt?: string; timezone?: string; actCount: number; stageCount: number;
    /** Artwork and the TEST mark, so a result is the app's own rave card (2026-11-02). */
    logoUrl?: string; coverUrl?: string; crowdSize?: number; test?: boolean;
  }[];
  performers: {
    kind: 'artist' | 'facilitator'; id: string; slug: string; name: string;
    countries: string[]; tags: string[]; appearanceCount: number; upcomingCount: number;
    /** The photo, so a result is the Artists page's poster. */
    photoUrl?: string;
  }[];
  /** Sets by their name or their artist's, across every rave (2026-11-02) — the schedule's row draws them. */
  sets?: {
    id: string; slug: string; name: string; startAt: string; endAt: string; timezone: string;
    stageName: string; stageColor: string; countries: string[]; genres: string[]; icon?: string; photoUrl?: string;
    subdomain: string; editionSlug: string; raveName: string;
  }[];
}

/** A rave's bios in one language, by performer id — only those translated (step 76.10). */
export type RaveBios = Record<string, { text: string; byGoogle: boolean }>;

export interface PerformerPage {
  kind: 'artist' | 'facilitator';
  id: string; slug: string; name: string;
  /** STUB / INVITED: a name and a booking so far — drawn as "profile coming". Absent from older APIs. */
  profileStatus?: 'STUB' | 'INVITED' | 'DRAFT' | 'PUBLISHED';
  bio?: string; countries: string[]; tags: string[];
  /** The bio is Google's translation into the reader's language (step 76). */
  bioTranslated?: boolean;
  links: Record<string, string>; photoUrl?: string; audioUrl?: string;
  /** A wide picture across the top of the page (2026-10-29), an artist's own. */
  coverUrl?: string;
  /** Somebody is already confirmed as this artist — no "This is me". Absent from older APIs. */
  claimed?: boolean;
  /** Followers, from 0 — the number, never who (step 76). Absent from older APIs. */
  followerCount?: number;
  upcoming: Appearance[];
  past: Appearance[];
  /** Step 58's full sets of their sets, newest first (step 76). Absent from older APIs. */
  fullSets?: PerformerFullSet[];
}

/** One artist this account follows (`GET /me/follows`): soonest next booking first (step 76). */
export interface FollowedArtist {
  id: string; slug: string; name: string; countries: string[]; followedAt: string;
  /** Their styles and follower count (step 76). Absent from older APIs. */
  genres?: string[]; followerCount?: number;
  /** Absent from older APIs, and for an artist with no photo. */
  photoUrl?: string;
  /** Their next booking with its first set; null = not booked right now. Absent from older APIs. */
  next?: {
    editionId: string; editionName: string; festivalName: string; subdomain: string; timezone: string;
    startAt: string | null; stageName: string | null; actSlug: string | null;
  } | null;
}

/** One artist on the Artists page — the platform's list or a rave's (step 76). */
export interface ArtistCard {
  id: string; slug: string; name: string; countries: string[]; genres: string[];
  photoUrl?: string; followerCount: number;
  /** Their first set in this rave (a rave's list only). */
  set: { startAt: string; endAt: string; stageName: string; actSlug: string } | null;
}
export interface ArtistList {
  artists: ArtistCard[];
  genres: { name: string; count: number }[];
  /** Their countries, the most artists first. Absent from older APIs. */
  countries?: { code: string; count: number }[];
  nextCursor: string | null;
}

export interface PerformerFullSet {
  id: string; title: string;
  /** YOUTUBE, SOUNDCLOUD, MIXCLOUD, OTHER. */
  platform: string; url: string;
  durationSec: number | null; publishedAt: string | null; official: boolean;
  editionName: string; festivalName: string; subdomain: string; actSlug: string | null;
}

/** One of this account's "This is me" requests, in whatever state it is in. */
export interface ArtistClaim {
  id: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'REVOKED';
  evidence: string | null;
  reviewNote: string | null;
  createdAt: string;
  artist: { id: string; slug: string; name: string };
}

export interface Appearance {
  editionId: string;
  festivalName: string; festivalSlug: string; subdomain: string;
  editionName: string; editionSlug: string; venue?: string; timezone: string;
  startAt: string | null; endAt: string | null;
  stageName: string | null; actSlug: string | null; isPast: boolean;
  /** Every set of the booking, in time order (step 76); the fields above are the first's. Absent from older APIs. */
  sets?: { actSlug: string; startAt: string; endAt: string; stageName: string }[];
}

/**
 * One attended edition, with the counters its dashboard card shows.
 *
 * The counters are computed by the API (`planStats` from `@myrave/shared`),
 * not here: working them out in the browser would mean downloading each
 * festival's whole bundle to render three numbers.
 */
export interface MyFestival {
  editionId: string;
  festivalId: string;
  editionName: string;
  editionSlug: string;
  festivalName: string;
  subdomain: string;
  gatesOpenAt: string | null;
  endsAt: string | null;
  timezone: string;
  attendingSince: string;
  /** The countdown module is on — the card's pill is its (§52.1). */
  countdown?: boolean;
  /** You hold a grant on this rave (festival admin or content manager): it is listed whether or not you attend. */
  managed?: boolean;
  /** False for a rave listed only because you run it. */
  attending?: boolean;
  venue: string | null;
  /** The rave's place, for the phone's arrival alert (step 60 task 4). */
  lat?: number | null;
  lng?: number | null;
  country: string | null;
  logoUrl: string | null;
  coverUrl: string | null;
  /** Acts saved to the plan. */
  saved: number;
  /** Pairs of saved acts that overlap. */
  clashes: number;
  /** Distinct festival days the plan touches. */
  days: number;
  /** How long the plan takes with overlaps counted once. */
  plannedMinutes: number;
  /** The published lineup's size — 0 while nothing is published. */
  actCount: number;
  stageCount: number;
}

export const endpoints = {
  tenant: () => api.get<{ tenant: null | { festivalSlug: string; festivalName: string; editionId: string | null; editionSlug: string | null; timezone: string | null; editions?: TenantEdition[]; coverUrl?: string | null; logoUrl?: string | null; country?: string | null; hidden?: boolean }; scope: 'platform' | 'festival'; auth?: { requiresAccount: boolean; requiresApproval: boolean } }>('/tenant'),
  festivals: () => api.get<{ festivals: FestivalCard[] }>('/festivals'),
  /**
   * One edition of THIS rave by its slug — the way the app opens a DRAFT for
   * its own admins (the setup wizard's preview). A 404 for everyone else.
   */
  editionBySlug: (slug: string) => api.get<TenantEdition & { status: string }>(`/editions/${encodeURIComponent(slug)}`),
  search: (q: string) => api.get<SearchResults>(`/search?q=${encodeURIComponent(q)}`),
  performer: (slug: string) => api.get<PerformerPage>(`/performers/${encodeURIComponent(slug)}`),
  /** A rave's translated bios in ONE language, for its offline copy (step 76.10). */
  raveBios: (editionId: string, lang: string) => api.get<{ language: string; bios: RaveBios }>(`/editions/${encodeURIComponent(editionId)}/bios?lang=${encodeURIComponent(lang)}`),
  attend: (editionId: string) => api.post<{ attending: boolean }>(`/editions/${editionId}/attend`),
  unattend: (editionId: string) => api.del(`/editions/${editionId}/attend`),
  myFestivals: () => myFestivalsStoredFirst(),
  /** The artists this account follows (plan.md §38.3). */
  follows: () => api.get<{ artists: FollowedArtist[] }>('/me/follows'),
  /** Follow several at once, after the review list (step 76). */
  followMany: (slugs: string[]) => api.post<{ following: number; unknown: number }>('/me/follows', { slugs }),
  /** The Artists page's two lists — one shape (step 76): every artist of an announced rave, or one rave's. */
  artists: (p: { genre?: string; country?: string; q?: string; cursor?: string; limit?: number }) => {
    const q = new URLSearchParams();
    for (const [k, v] of Object.entries(p)) if (v !== undefined && v !== '') q.set(k, String(v));
    return api.get<ArtistList>(`/artists${q.size ? `?${q}` : ''}`);
  },
  raveArtists: (editionId: string) => api.get<ArtistList>(`/editions/${encodeURIComponent(editionId)}/artists`),
  follow: (artistSlug: string) => api.post<{ following: boolean }>(`/me/follows/${encodeURIComponent(artistSlug)}`),
  unfollow: (artistSlug: string) => api.del(`/me/follows/${encodeURIComponent(artistSlug)}`),

  /** "This is me" (plan.md §16.2): anyone signed in may ask; a platform admin decides. */
  claimArtist: (artistSlug: string, input: { evidence?: string; links?: Record<string, string> }) =>
    api.post<{ claim: ArtistClaim }>(`/artists/${encodeURIComponent(artistSlug)}/claim`, input),
  myArtistClaims: () => api.get<{ claims: ArtistClaim[] }>('/me/artist-claims'),

  /** Your tickets (step 82) — each person's own; adding needs attending the rave (403 `attend_first`). */
  myTickets: () => api.get<{ raves: import('@/visitor/tickets/ticketRules').TicketRave[]; tickets: import('@/visitor/tickets/ticketRules').Ticket[] }>('/me/tickets'),
  raveTickets: (editionId: string) =>
    api.get<{ tickets: import('@/visitor/tickets/ticketRules').Ticket[] }>(`/editions/${encodeURIComponent(editionId)}/tickets`),
  /** A form: `file` (the original) + `codes` (JSON: format, value, binary, page, name, description). */
  addTickets: (editionId: string, form: FormData) =>
    api.postForm<{ tickets: import('@/visitor/tickets/ticketRules').Ticket[] }>(`/editions/${encodeURIComponent(editionId)}/tickets`, form),
  renameTicket: (id: string, patch: { name?: string; description?: string | null; details?: import('@myrave/shared').TicketDetails | null }) => api.patch<void>(`/me/tickets/${encodeURIComponent(id)}`, patch),
  deleteTicket: (id: string) => api.del<void>(`/me/tickets/${encodeURIComponent(id)}`),
  /** The original file, to its owner only (`private, no-store`). */
  ticketFile: (originalId: string) => api.getBlob(`/me/ticket-files/${encodeURIComponent(originalId)}`),
};

/**
 * **The person's raves — the network first, but never waited on for ever**
 * (the user, 2026-10-14: a festival signal is often ON but useless, "the app
 * thinks there is internet access then everything fails — use the offline as
 * well"). The pilot's rule, as the bundle and `/tenant` keep it: after
 * `NETWORK_TIMEOUT_MS`, or on a failure or a 5xx, the last list THIS ACCOUNT
 * got (`myrave:platform:my-raves-<account>`). A refusal (401/403/404) is an
 * answer, never "offline". Nothing stored — a first visit — waits for the
 * network. The answer still lands and is kept for next time.
 */
/** `fromDevice`: the phone's copy, shown while the network is slow — never the server's own answer. */
type MyFestivals = { festivals: MyFestival[]; fromDevice?: boolean };

/**
 * The list this account last got, kept on the phone — at once, with no wait: the bottom bar draws
 * from it on its first frame, so LIVE and the counts are there right away and never pop in when
 * the network answers (the user, 2026-10-29: "if there is Live, it needs to be right away and not
 * switched"). Before the session is restored the device's own sign-in record names the account;
 * signed out there is none, and nothing is read.
 */
export function storedMyFestivals(): MyFestivals | undefined {
  const who = currentAccountId() ?? signedInHere();
  const kept = who ? readJson<MyFestivals | null>(null, `my-raves-${who}`, null) : null;
  // Raves this address's copy is missing but the device keeps for offline (`keptRaves.ts`).
  return kept ? { ...kept, festivals: withKeptRaves(kept.festivals ?? [], who) } : undefined;
}

/**
 * The network's answer that arrived AFTER the phone's copy was shown (the user, 2026-10-29: signed
 * in, My raves showed none until a reload showed three). It was kept for next time but never
 * reached the screen, which held the copy for the query's whole stale time. `AuthProvider` hands
 * it to the query cache.
 */
const lateMyFestivals = new Set<(answer: MyFestivals) => void>();
export function onLateMyFestivals(listener: (answer: MyFestivals) => void): () => void {
  lateMyFestivals.add(listener);
  return () => { lateMyFestivals.delete(listener); };
}

async function myFestivalsStoredFirst(): Promise<MyFestivals> {
  const who = currentAccountId();
  const name = who ? `my-raves-${who}` : null;
  const net = api.get<MyFestivals>('/me/festivals').then((r) => {
    if (name) writeJson(null, name, r);
    // Every address on the device learns which raves are kept for offline (`keptRaves.ts`).
    if (who) noteKeptRaves(who, r.festivals ?? []);
    return r;
  });
  const kept = name ? readJson<MyFestivals | null>(null, name, null) : null;
  // With the raves another address heard of since this copy was kept (w-offline-per-edition, 2026-11-02).
  const fromCookie = kept ? [] : keptRaves(who);
  const stored: MyFestivals | null = kept ? { festivals: withKeptRaves(kept.festivals ?? [], who), fromDevice: true }
    : fromCookie.length ? { festivals: fromCookie, fromDevice: true } : null;
  if (!stored) return net;
  // An EMPTY copy has nothing to show, and "no raves" sends My raves away to All raves: wait for the
  // network, and fall back to it only when there is none (the user, 2026-10-29: "still no").
  if (stored.festivals.length === 0) {
    try { return await net; } catch (err) {
      // With a network a failure is a failure ("try again"), never an empty copy to wait on for ever.
      if ((err instanceof ApiError && err.status < 500) || (typeof navigator !== 'undefined' && navigator.onLine !== false)) throw err;
      return stored;
    }
  }
  net.catch(() => {});
  let timer: ReturnType<typeof setTimeout> | undefined;
  const late = new Promise<null>((ok) => { timer = setTimeout(() => ok(null), NETWORK_TIMEOUT_MS); });
  try {
    const first = await Promise.race([net, late]);
    if (first) return first;
    // The copy is shown; the network's answer follows it onto the screen when it lands.
    net.then((r) => { lateMyFestivals.forEach((l) => l(r)); }, () => {});
    return stored;
  } catch (err) {
    if (err instanceof ApiError && err.status < 500) throw err;
    return stored;
  } finally {
    clearTimeout(timer);
  }
}
